import { useUpdateEffect } from '@cian/react-utils';

import { useCallback, useRef } from 'react';

import { useMapStoreContext } from '@/microfrontends/map/shared/context/mapStoreContext';
import { isGeoChanged } from '@/microfrontends/map/shared/utils/isGeoChanged';

import { type TJsonQuery } from '@/packages/JsonQuery';
import { useGetClustersQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/query';
import { useMapGetBboxQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/map/get-bbox/query';
import { mapPreparedMapBboxSelector } from '@/packages/query-state/shared/store/search-offers-index/v2/map/get-bbox/selectors';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { useFiltersSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { useListingLayoutIsNarrowSelector } from '@/packages/redux-state/store/slices/listingLayout';
import { useMapConfigurationSelector } from '@/packages/redux-state/store/slices/mapConfiguration';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setMapConfiguration: reduxActions.mapConfiguration.setMapConfiguration,
}));

export function useMapUpdateOnFiltersChange(): void {
  const { map } = useMapStoreContext();
  const { initialized } = useMapConfigurationSelector();
  const isNarrowListing = useListingLayoutIsNarrowSelector();
  const actions = useBoundActions();

  const { refetch: refetchMapBbox } = useMapGetBboxQuery({
    enabled: initialized && isNarrowListing,
    select: mapPreparedMapBboxSelector,
  });

  const { refetch: refetchMapClusters } = useGetClustersQuery();

  const filters = useFiltersSelector();
  const prevFilters = useRef<TJsonQuery>(filters);

  const updateMapConfiguration = useCallback(async () => {
    const { data: mapBbox } = await refetchMapBbox();

    if (mapBbox) {
      await map.setBounds(mapBbox, { zoomMargin: 100 });

      actions.setMapConfiguration({
        bounds: map.getBounds({ useMapMargin: true }),
        center: map.getCenter(),
        zoom: map.getZoom(),
      });

      refetchMapClusters();
    }
  }, [refetchMapBbox, map, actions, refetchMapClusters]);

  useUpdateEffect(() => {
    if (!isNarrowListing) {
      return;
    }

    if (isGeoChanged(prevFilters.current, filters)) {
      updateMapConfiguration();
    } else {
      refetchMapClusters();
    }

    prevFilters.current = filters;
  }, [filters, isNarrowListing]);
}
